import { Check, ChevronLeft, ChevronRight, X } from "@/components/ui/icons";
import { createContext, type ReactNode, useContext, useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Sheet, SheetScroll } from "@/components/ui/Sheet";
import { type Facet, facetValueLabel, isFacetApplied } from "@/lib/filters/facet";
import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";

/** Past this many options a facet's picker gets a search field, as a large book's Set list needs. */
const SEARCH_AFTER = 8;

type FacetNav = { page: string | null; setPage: (page: string | null) => void; close: () => void; };

const FacetNavContext = createContext<FacetNav | null>(null);

/**
 * The phone filter sheet every filtered list shares (inventory, labels, the storefronts): a modal
 * sheet whose action names the effect ("Show 87 items"), holding a `FacetPanel`.
 *
 * The panel is a child rather than built in, so a screen can read what only the open sheet needs
 * (inventory's Collections) in a component that mounts with it.
 */
export function FacetSheet(
  { visible, onClose, actionLabel = "Done", hosted = false, children }: {
    visible: boolean;
    onClose: () => void;
    actionLabel?: string;
    /** Render into an ancestor's native Modal (a full-screen search) instead of opening another. */
    hosted?: boolean;
    children: ReactNode;
  },
) {
  const [page, setPage] = useState<string | null>(null);
  const close = () => {
    setPage(null);
    onClose();
  };
  return (
    <Sheet
      visible={visible}
      onClose={close}
      // Rows are 48pt each, so the taxonomy fits without a scroll on a standard phone, and the
      // chips under the search field already say what is on.
      ceiling={0.8}
      footer={<Button title={actionLabel} onPress={close} />}
      hosted={hosted}
    >
      <FacetNavContext.Provider value={{ page, setPage, close }}>
        {children}
      </FacetNavContext.Provider>
    </Sheet>
  );
}

/** A labelled inline section above the summary rows, for the short, most-used choices. */
export function FacetGroup({ label, children }: { label: string; children: ReactNode; }) {
  return (
    <View className="gap-2">
      <Text
        className="font-label text-xs uppercase tracking-wide text-subtle"
        accessibilityRole="header"
        // react-native-web renders an unleveled `header` as `<h1>`.
        aria-level={3}
      >
        {label}
      </Text>
      <View className="flex-row flex-wrap gap-2">{children}</View>
    </View>
  );
}

/**
 * The filters as a summary row per facet that drills into that facet's options.
 *
 * Each row names its current value, so the whole taxonomy reads on one screen and a facet added
 * later costs one row rather than another block of pills. `inline` holds the few choices a screen
 * keeps one tap away above the rows (inventory's Status and Type).
 *
 * `testIDPrefix` names the rows for a screen's harness rungs (`inventory-facet-set`).
 */
export function FacetPanel<P>(
  {
    facets,
    onChange,
    inline,
    appliedCount,
    onClearAll,
    optionsFooter,
    title = "Filters",
    testIDPrefix,
  }: {
    facets: Facet<P>[];
    onChange: (patch: P) => void;
    inline?: ReactNode;
    appliedCount: number;
    onClearAll: () => void;
    /** Rendered under one facet's options (inventory's "Manage lists"). */
    optionsFooter?: (facet: Facet<P>, close: () => void) => ReactNode;
    title?: string;
    testIDPrefix: string;
  },
) {
  const nav = useContext(FacetNavContext);
  if (!nav) throw new Error("FacetPanel renders inside a FacetSheet");
  const { page, setPage, close } = nav;
  const c = useThemeColors();
  const open = facets.find((f) => f.key === page);

  if (open) {
    return (
      <>
        <View className="mb-3 flex-row items-center gap-2">
          <Pressable
            onPress={() => setPage(null)}
            testID={`${testIDPrefix}-facet-back`}
            accessibilityRole="button"
            accessibilityLabel="Back to all filters"
            hitSlop={4}
            className="h-9 w-9 items-center justify-center rounded-lg active:bg-surface-muted"
          >
            <ChevronLeft size={20} color={c.foreground} />
          </Pressable>
          <Text
            accessibilityRole="header"
            aria-level={2}
            className="flex-1 font-title text-lg text-foreground"
          >
            {open.label}
          </Text>
        </View>
        <FacetOptions
          facet={open}
          testIDPrefix={testIDPrefix}
          onPick={(value) => {
            const multi = open.multi;
            if (multi && value !== null) {
              // A multi-select facet stays open so several values can be ticked in one visit.
              onChange(multi.toggle(value));
              return;
            }
            onChange(open.pick(value));
            setPage(null);
          }}
          footer={optionsFooter?.(open, close)}
        />
      </>
    );
  }

  return (
    <>
      <View className="mb-4 flex-row items-center justify-between">
        <Text
          className="text-lg font-title text-foreground"
          accessibilityRole="header"
          aria-level={2}
        >
          {title}
        </Text>
        <View className="flex-row items-center gap-1">
          {appliedCount > 0
            ? <Button title="Clear all" variant="ghost" size="sm" onPress={onClearAll} />
            : null}
          <Pressable
            onPress={close}
            testID={`${testIDPrefix}-filters-close`}
            accessibilityRole="button"
            accessibilityLabel="Close filters"
            hitSlop={4}
            className="h-9 w-9 items-center justify-center rounded-lg active:bg-surface-muted"
          >
            <X size={18} color={c.foreground} />
          </Pressable>
        </View>
      </View>
      <SheetScroll contentContainerClassName="gap-5 pb-2">
        {inline}
        <View>
          {facets.map((f) => {
            const value = facetValueLabel(f);
            const on = isFacetApplied(f);
            return (
              <Pressable
                key={f.key}
                testID={`${testIDPrefix}-facet-${f.key}`}
                onPress={() => setPage(f.key)}
                accessibilityRole="button"
                accessibilityLabel={`${f.label}, ${value}`}
                accessibilityHint={`Opens ${f.label.toLowerCase()} options`}
                className="min-h-12 flex-row items-center gap-3 border-b border-border py-2"
              >
                <Text className="flex-1 font-body text-base text-foreground">{f.label}</Text>
                <Text
                  numberOfLines={1}
                  className={cn(
                    "max-w-[55%] font-label text-sm",
                    on ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  {value}
                </Text>
                <ChevronRight size={16} color={c.mutedForeground} aria-hidden />
              </Pressable>
            );
          })}
        </View>
      </SheetScroll>
    </>
  );
}

/** One facet's options as a checked list, its resting choice first. */
function FacetOptions<P>(
  { facet, onPick, footer, testIDPrefix }: {
    facet: Facet<P>;
    onPick: (value: string | null) => void;
    footer?: ReactNode;
    testIDPrefix: string;
  },
) {
  const c = useThemeColors();
  const [query, setQuery] = useState("");
  const shown = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    return q
      ? facet.options.filter((o) => o.label.toLocaleLowerCase().includes(q))
      : facet.options;
  }, [facet.options, query]);
  const choices: { value: string | null; label: string; a11yLabel?: string; }[] = [
    { value: facet.restingValue, label: facet.restingLabel },
    ...shown.filter((option) => option.value !== facet.restingValue),
  ];
  const isOn = (value: string | null) =>
    facet.multi
      ? value === null ? facet.multi.values.length === 0 : facet.multi.values.includes(value)
      : facet.value === value;

  return (
    <>
      {facet.searchable && facet.options.length > SEARCH_AFTER
        ? (
          <View className="mb-2">
            <Input
              testID={`${testIDPrefix}-facet-${facet.key}-search`}
              value={query}
              onChangeText={setQuery}
              placeholder={`Find a ${facet.label.toLowerCase()}…`}
              accessibilityLabel={`Find a ${facet.label.toLowerCase()}`}
              clearButtonMode="while-editing"
            />
          </View>
        )
        : null}
      <SheetScroll contentContainerClassName="pb-2">
        {choices.map((o) => {
          const on = isOn(o.value);
          return (
            <Pressable
              key={o.value ?? "__resting"}
              onPress={() => onPick(o.value)}
              accessibilityRole="button"
              accessibilityLabel={o.a11yLabel ?? o.label}
              aria-pressed={on}
              accessibilityState={{ selected: on }}
              className="min-h-12 flex-row items-center gap-3 border-b border-border py-2"
            >
              <Text
                className={cn(
                  "flex-1 font-body text-base",
                  on ? "font-label text-primary" : "text-foreground",
                )}
              >
                {o.label}
              </Text>
              {on ? <Check size={18} color={c.primary} aria-hidden /> : null}
            </Pressable>
          );
        })}
        {shown.length === 0
          ? <Text className="py-4 font-body text-sm text-muted-foreground">No matches.</Text>
          : null}
        {footer}
      </SheetScroll>
    </>
  );
}
